import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Icon } from './Icon';

export interface KpiTileProps {
  label: string;
  /** Optional: ScrAbsences / ScrFinance tiles have no icon. */
  icon?: string;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'ok' | 'warn' | 'err' | 'neutral';
  note?: ReactNode;
  /** 0–100; renders the 4px progress bar when set. */
  progress?: number;
  onClick?: () => void;
  loading?: boolean;
}

const DELTA = {
  ok: 'success.main',
  warn: 'warning.main',
  err: 'error.main',
  neutral: 'text.primary',
} as const;

export function KpiTile({
  label,
  icon,
  value,
  delta,
  deltaTone = 'neutral',
  note,
  progress,
  onClick,
  loading,
}: KpiTileProps) {
  const body = loading ? (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Skeleton variant="rounded" width={120} height={12} sx={{ borderRadius: '6px' }} />
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
      </Box>
      <Skeleton variant="rounded" width={88} height={28} sx={{ borderRadius: '6px' }} />
      <Skeleton variant="rounded" width={140} height={10} sx={{ borderRadius: '5px' }} />
    </>
  ) : (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{label}</Typography>
        {icon && (
          <Box
            sx={(t) => ({
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: t.vars.palette.primary.container,
              color: t.vars.palette.primary.onContainer,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <Icon name={icon} />
          </Box>
        )}
      </Box>
      <Typography variant="kpi">{value}</Typography>
      {progress !== undefined && (
        <Box
          sx={{
            height: 4,
            width: '100%',
            borderRadius: '2px',
            bgcolor: 'surface.containerHigh',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(100, Math.max(0, progress))}%`,
              bgcolor: 'primary.main',
              borderRadius: '2px',
            }}
          />
        </Box>
      )}
      {(delta ?? note) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 12,
            lineHeight: '16px',
            flexWrap: 'wrap',
          }}
        >
          {delta && (
            <Box component="span" sx={{ color: DELTA[deltaTone], fontWeight: 600 }}>
              {delta}
            </Box>
          )}
          {note && (
            <Box component="span" sx={{ color: 'text.tertiary' }}>
              {note}
            </Box>
          )}
        </Box>
      )}
    </>
  );

  const sx = {
    textAlign: 'start',
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '10px',
    color: 'text.primary',
    width: '100%',
  } as const;

  return onClick && !loading ? (
    <ButtonBase
      onClick={onClick}
      sx={{ ...sx, '&:hover': { borderColor: 'outline', boxShadow: '0 2px 10px rgba(0,0,0,.06)' } }}
    >
      {body}
    </ButtonBase>
  ) : (
    <Box sx={sx} aria-busy={loading || undefined}>
      {body}
    </Box>
  );
}
